import { GetContract } from "@/services/fhir/contract/GetContract";
import React, {
    createContext,
    useContext,
    useState,
    useEffect,
    useCallback,
} from "react";
import { useOrganizationContext } from "./OrganizationContext";
import { Contract } from "fhir/r5";

type ContractResource = Contract;

interface QuestionnairesContractContextValue {
    contractQuestionnaires: ContractResource | null;
    refetchContract: () => Promise<void>;
    loading: boolean;
}

interface QuestionnairesContractContextProviderProps {
    children: React.ReactNode;
}

export const QuestionnairesContractContext =
    createContext<QuestionnairesContractContextValue>(
        {} as QuestionnairesContractContextValue
    );

export function QuestionnairesContractContextProvider({
    children,
}: QuestionnairesContractContextProviderProps) {
    const { organization } = useOrganizationContext();
    const [contractQuestionnaires, setContractQuestionnaires] = useState<ContractResource | null>(null);
    const [loading, setLoading] = useState(false);

    const fetchContract = useCallback(async () => {
        setLoading(true);
        try {
            if (!organization?.id) {
                console.warn("Nenhuma organização selecionada");
                setContractQuestionnaires(null);
                return;
            }

            const contractData = await GetContract({
                organization: organization.id,
                count: 1,
                sort: true,
                status: "executed",
                identifier: "questionnaires",
            });

            if (contractData?.entry?.[0]?.resource) {
                const resource = contractData.entry?.[0]?.resource;
                setContractQuestionnaires(resource);
            } else {
                setContractQuestionnaires(null);
            }
        } catch (error) {
            console.error("Erro ao buscar contrato de questionários:", error);
            setContractQuestionnaires(null);
        } finally {
            setLoading(false);
        }
    }, [organization]);

    useEffect(() => {
        fetchContract();
    }, [fetchContract]);

    const contextValue: QuestionnairesContractContextValue = {
        loading,
        contractQuestionnaires,
        refetchContract: fetchContract,
    };

    return (
        <QuestionnairesContractContext.Provider value={contextValue}>
            {children}
        </QuestionnairesContractContext.Provider>
    );
}

export function useQuestionnairesContractContext() {
    return useContext(QuestionnairesContractContext);
}

